import { PointerEvent, useLayoutEffect, useRef } from 'react';
import { Box } from '@mui/material';
import BarcodeSvg from './BarcodeSvg';
import BlockActions from './BlockActions';
import { CashdeskBlock } from './cashdeskBlocks';

interface TicketBlockProps {
  block: CashdeskBlock;
  selected: boolean;
  onMoveStart: (e: PointerEvent<HTMLDivElement>) => void;
  onResizeStart: (e: PointerEvent<HTMLDivElement>) => void;
  onUpdate: (patch: Partial<CashdeskBlock>) => void;
  onRemove: () => void;
}

function BlockContent({ block, onUpdate }: Pick<TicketBlockProps, 'block' | 'onUpdate'>) {
  const textRef = useRef<HTMLDivElement>(null);

  // Uncontrolled contentEditable: only sync the DOM when content changes externally
  // (e.g. variable insertion), otherwise React re-renders reset the caret position.
  useLayoutEffect(() => {
    const el = textRef.current;
    if (el && el.innerText !== (block.content ?? '')) el.innerText = block.content ?? '';
  }, [block.content]);

  if (block.type === 'image') {
    return (
      <Box
        component="img"
        src={block.src}
        draggable={false}
        sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', pointerEvents: 'none' }}
      />
    );
  }
  if (block.type === 'barcode') return <BarcodeSvg label={block.anchor ?? ''} width={block.width} height={block.height} />;
  return (
    <Box
      ref={textRef}
      component="div"
      contentEditable
      suppressContentEditableWarning
      onInput={e => onUpdate({ content: e.currentTarget.innerText })}
      sx={{
        width: '100%',
        height: '100%',
        padding: '2px 4px',
        boxSizing: 'border-box',
        fontSize: block.fontSize,
        fontWeight: block.fontWeight,
        fontStyle: block.fontStyle,
        textDecoration: block.textDecoration,
        textAlign: block.textAlign,
        outline: 'none',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        overflow: 'hidden',
        lineHeight: 1.4,
        // Rendu du ticket de caisse imprimé : couleur littérale
        color: '#212529',
        cursor: 'text',
      }}
    />
  );
}

function TicketBlock({ block, selected, onMoveStart, onResizeStart, onUpdate, onRemove }: TicketBlockProps) {
  return (
    <Box
      sx={{
        position: 'absolute',
        left: block.x,
        top: block.y,
        width: block.width,
        height: block.height,
        transform: `rotate(${block.rotation}deg)`,
        zIndex: block.zIndex,
        border: selected ? '2px solid' : '1px dashed',
        borderColor: selected ? 'primary.main' : 'divider',
        cursor: 'move',
        boxSizing: 'border-box',
        // Papier du ticket
        backgroundColor: '#fff',
      }}
      onPointerDown={onMoveStart}
      onClick={e => e.stopPropagation()}
    >
      <BlockContent block={block} onUpdate={onUpdate} />

      {selected && block.type !== 'barcode' && (
        <Box
          onPointerDown={onResizeStart}
          sx={{
            position: 'absolute',
            right: -5,
            bottom: -5,
            width: 10,
            height: 10,
            borderRadius: '2px',
            backgroundColor: 'primary.main',
            border: '1px solid',
            borderColor: 'common.white',
            cursor: 'nwse-resize',
            touchAction: 'none',
          }}
        />
      )}

      {selected && <BlockActions block={block} onUpdate={onUpdate} onRemove={onRemove} />}
    </Box>
  );
}

export default TicketBlock;
